import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { colors } from '../../../theme/palette'
import { Typography } from '../../ui/Typography/Typography'
import { Select } from '../../ui/FormElememts/Select'
import { Input } from '../../ui/FormElememts/Input'
import { Button } from '../../ui/Button/Button'
import { CircleIcon2 } from '../../ui/Circle/Circle'
import { Close } from '../../../assets/icons'
import { PercentCircle } from '../../ui/PercentLevel/PercentLevel'
import { L, L_HTML } from '../../providers/Lokalise'
import { Spinner } from '../../ui/Loaders/Spinner'
import { updateProfileUserCovers } from '../../../api/profile/profile'
import { IUpdateMyProfileUserCovers } from '../EditMyProfile'
import { WarningDialog } from '../WarningDialog'

const useStyles = createUseStyles({
    editIconWrapper: {
        top: '0',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'end',
    },
    confirmButtons: {
        display: 'flex',
        zIndex: '1',
        position: 'relative',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        padding: '20px 0 0',
    },
})

interface OrganizationWidgetProps {
    data: {
        numberFoaMemberUserCoversTR: number
        numberFoaMemberUserCoversFTR: number
        totalNumberOfPeopleUserCoversTR: number
        totalNumberOfPeopleUserCoversFTR: number
    }
    authProfile: {
        isImitator: boolean
    }
    onUpdate: () => void
    onImitatorModal: () => void
}

const OrganizationWidget: React.FC<OrganizationWidgetProps> = ({
    data,
    authProfile,
    onUpdate,
    onImitatorModal,
}) => {
    const classes = useStyles()
    const [showWarningDialog, setShowWarningDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [percentageState, setPercentageState] = useState<'TR' | 'FTR'>('TR')
    const [percentageShowEdit, setPercentageShowEdit] = useState(false)
    const [percentageEditValueTR, setPercentageEditValueTR] = useState<
        number | undefined
    >(undefined)
    const [percentageEditValueFTR, setPercentageEditValueFTR] = useState<
        number | undefined
    >(undefined)
    const [percentageOfMembersCoveredTR, setPercentageOfMembersCoveredTR] =
        useState(0)
    const [percentageOfMembersCoveredFTR, setPercentageOfMembersCoveredFTR] =
        useState(0)

    const calcPercentageCoveredInEditMode = (
        value: number,
        numberFoaMemberUserCovers: number
    ): number => {
        if (value < numberFoaMemberUserCovers || 0) {
            return 100
        }
        return (numberFoaMemberUserCovers / value) * 100
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calcPercentageCovered = (
        numberFoaMemberUserCovers: number,
        totalNumberOfPeopleUserCovers: number
    ): number => {
        return (numberFoaMemberUserCovers / totalNumberOfPeopleUserCovers) * 100
    }

    useEffect(() => {
        setPercentageOfMembersCoveredTR(
            data.totalNumberOfPeopleUserCoversTR !== null
                ? calcPercentageCovered(
                      data.numberFoaMemberUserCoversTR,
                      data.totalNumberOfPeopleUserCoversTR
                  )
                : 0
        )
        setPercentageOfMembersCoveredFTR(
            data.totalNumberOfPeopleUserCoversFTR !== null
                ? calcPercentageCovered(
                      data.numberFoaMemberUserCoversFTR,
                      data.totalNumberOfPeopleUserCoversFTR
                  )
                : 0
        )
        setPercentageShowEdit(false)
        setPercentageEditValueTR(undefined)
        setPercentageEditValueFTR(undefined)
    }, [data])

    const getTotalCount = (): string => {
        if (percentageShowEdit) {
            return String(
                percentageState === 'TR'
                    ? percentageEditValueTR || 0
                    : percentageEditValueFTR || 0
            )
        }
        return String(
            percentageState === 'TR'
                ? data.totalNumberOfPeopleUserCoversTR
                : data.totalNumberOfPeopleUserCoversFTR
        )
    }

    const handleProfileUpdate = async (): Promise<void> => {
        if (percentageState === 'TR' && percentageEditValueTR) {
            if (percentageEditValueTR < data.numberFoaMemberUserCoversTR) {
                setShowWarningDialog(true)
                return
            }
        } else if (percentageState === 'FTR' && percentageEditValueFTR) {
            if (percentageEditValueFTR < data.numberFoaMemberUserCoversFTR) {
                setShowWarningDialog(true)
                return
            }
        }

        const fieldsToEdit: IUpdateMyProfileUserCovers = {}
        if (percentageState === 'TR' && percentageEditValueTR) {
            fieldsToEdit.TotalAntalKollegaerBrugerDaekkerTR =
                percentageEditValueTR
        } else if (percentageState === 'FTR' && percentageEditValueFTR) {
            fieldsToEdit.TotalAntalKollegaerBrugerDaekkerFTR =
                percentageEditValueFTR
        }

        setLoading(true)
        const response = await updateProfileUserCovers(fieldsToEdit)
        setLoading(false)

        if (response) {
            onUpdate()
        }
    }

    return (
        <div style={{ maxWidth: '100%' }}>
            <Typography size="S" style={{ marginBottom: '20px' }}>
                {L_HTML('TRPROFIL_ORGANISERINGSWIDGET_TEKST_1', {
                    rolle: percentageState === 'TR' ? 'TR' : 'FTR',
                })}
            </Typography>
            <Select
                defaultValue="TR"
                value={percentageState}
                placeholder="Tillidshverv"
                onChange={(e: any) => {
                    setPercentageState(e.target.value)
                    setPercentageShowEdit(false)
                    setPercentageEditValueTR(undefined)
                    setPercentageEditValueFTR(undefined)
                }}
                options={[
                    { label: 'TR', value: 'TR' },
                    { label: 'FTR', value: 'FTR' },
                ]}
            />
            <div
                style={{
                    position: 'relative',
                    margin: '10px 0 20px',
                    display: 'block',
                }}
            >
                {!percentageShowEdit ? (
                    <>
                        <Input
                            label={L(
                                'TRPROFIL_ORGANISERINGSWIDGET_INPUT_TEKST'
                            )}
                            value={
                                percentageState === 'TR'
                                    ? data.totalNumberOfPeopleUserCoversTR
                                    : data.totalNumberOfPeopleUserCoversFTR
                            }
                            required={false}
                        />
                        <div className={classes.editIconWrapper}>
                            <Button
                                type="action"
                                style={{
                                    marginRight: '10px',
                                    height: '50px',
                                    backgroundColor: 'transparent',
                                }}
                                onClick={() => {
                                    if (authProfile.isImitator) {
                                        onImitatorModal()
                                        return
                                    }
                                    setPercentageShowEdit(true)
                                }}
                            >
                                <CircleIcon2 width="24px">
                                    <Close
                                        width="16px"
                                        color={colors.green500}
                                    />
                                </CircleIcon2>
                            </Button>
                        </div>
                    </>
                ) : null}

                {percentageShowEdit ? (
                    <div>
                        <div>
                            {loading ? <Spinner /> : null}
                            <Input
                                label="Indtast tal"
                                autoFocus
                                value={
                                    percentageState === 'TR'
                                        ? percentageEditValueTR
                                        : percentageEditValueFTR
                                }
                                required={false}
                                onChange={(e) => {
                                    const val = e.target.value
                                    if (percentageState === 'TR') {
                                        setPercentageEditValueTR(+val)
                                        setPercentageOfMembersCoveredTR(
                                            calcPercentageCoveredInEditMode(
                                                +val,
                                                data.numberFoaMemberUserCoversTR
                                            )
                                        )
                                    } else {
                                        setPercentageEditValueFTR(+val)
                                        setPercentageOfMembersCoveredFTR(
                                            calcPercentageCoveredInEditMode(
                                                +val,
                                                data.numberFoaMemberUserCoversFTR
                                            )
                                        )
                                    }
                                }}
                            />
                        </div>
                        <div className={classes.confirmButtons}>
                            <Button
                                onClick={() => {
                                    setPercentageShowEdit(false)
                                    setPercentageEditValueFTR(undefined)
                                    setPercentageEditValueTR(undefined)
                                }}
                                size="xlarge"
                                type="secondary"
                                style={{ flexGrow: 1 }}
                            >
                                Annuller
                            </Button>
                            <Button
                                onClick={async () => {
                                    if (authProfile.isImitator) {
                                        onImitatorModal()
                                        return
                                    }

                                    handleProfileUpdate()
                                }}
                                style={{ flexGrow: 1 }}
                                size="xlarge"
                            >
                                Gem
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>

            <PercentCircle
                size={250}
                strokeWidth={20}
                percentage={
                    percentageState === 'TR'
                        ? percentageOfMembersCoveredTR
                        : percentageOfMembersCoveredFTR
                }
            />
            <Typography size="S" align="center" style={{ margin: '20px 0' }}>
                {percentageState === 'TR'
                    ? L_HTML('TRPROFIL_ORGANISERINGSWIDGET_TEKST_2_TR', {
                          ProcentDaekker: String(
                              Math.round(percentageOfMembersCoveredTR) || 0
                          ),
                          AntalFoaMedlemmerBrugerDaekker: String(
                              data.numberFoaMemberUserCoversTR
                          ),
                          TotalAntalKollegaerBrugerDaekker: getTotalCount(),
                      })
                    : L_HTML('TRPROFIL_ORGANISERINGSWIDGET_TEKST_2_FTR', {
                          ProcentDaekker: String(
                              Math.round(percentageOfMembersCoveredFTR) || 0
                          ),
                          AntalFoaMedlemmerBrugerDaekker: String(
                              data.numberFoaMemberUserCoversFTR
                          ),
                          TotalAntalKollegaerBrugerDaekker: getTotalCount(),
                      })}
            </Typography>
            <WarningDialog
                show={showWarningDialog}
                title={L('ORGANISERINGS_DIALOG_ADAVARSEL_TITEL')}
                description={L('ORGANISERINGS_DIALOG_ADAVARSEL_TEKST')}
                buttonText={L('ORGANISERINGS_DIALOG_ADAVARSEL_KNAP')}
                onClose={() => setShowWarningDialog(false)}
            />
        </div>
    )
}

export { OrganizationWidget }
